import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { MobileDeviceType } from '~/types/device-type'
import type { ITemplateParams } from '~/types/template'

import AppTemplateSelectorModal from './AppTemplateSelector'

import { AppSelectorDropdown } from '~/components/AppSelector'
import BackNavigation from '~/components/BackNavigation'
import ResetWarningModal from '~/components/ResetWarningModal'
import { useAppsListQuery } from '~/queries/applications'

import './styles.scss'

interface Props {
  backNavigationUrl: string
  id?: string
  isTemplateModalOpen?: boolean
  onAppRowClick: (app: { app_key: string; device_type: MobileDeviceType }) => void
  onTemplateClick: (params: ITemplateParams) => void
  selectedApp?: IAppSummary
}

const DynamicScreenHeader = ({
  backNavigationUrl,
  id,
  isTemplateModalOpen,
  onAppRowClick,
  onTemplateClick,
  selectedApp,
}: Props): ReactElement => {
  const navigate = useNavigate()

  const { state }: { state?: ITemplateParams } = useLocation()

  const { data: appsData } = useAppsListQuery({ owned: true })

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [resetModalOpen, setResetModalOpen] = useState(false)

  const isCreation = id === 'creation'

  const apps = appsData?.data?.filter(({ device_type }) =>
    state?.deviceType ? device_type === state.deviceType : true
  )

  const handleModalOpen = () => {
    selectedApp ? setResetModalOpen(true) : setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    navigate('/dynamic-screens/page-containers')
  }

  const handleTemplateClick = (params: ITemplateParams) => {
    onTemplateClick(params)
    setIsModalOpen(false)
  }

  const handleAppRowClick = (app: { app_key: string; device_type: MobileDeviceType }) => {
    onAppRowClick(app)
    state && handleTemplateClick(state)
  }

  const handleResetModalClose = () => setResetModalOpen(false)

  const handleResetModalConfirm = () => {
    setResetModalOpen(false)
    setIsModalOpen(true)
  }

  useEffect(() => {
    isCreation && setIsModalOpen(true)
  }, [isCreation, isTemplateModalOpen])

  useEffect(() => {
    return window.history.replaceState({}, document.title)
  }, [])

  const appSelectorApp = selectedApp
    ? {
        app_key: selectedApp.app_key,
        default_display_name: selectedApp.name,
        device_type: selectedApp.device_type,
        icon_file_id: selectedApp.app_icon_url.replace('https://fastly.mwm-storage.mwmcdn.com/raw_files/', ''),
      }
    : undefined

  return (
    <div className="dynamic-screen-header">
      <div className="header-details">
        <BackNavigation backgroundColor="dark" title="Back" to={backNavigationUrl} />
        <AppSelectorDropdown
          apps={appSelectorApp ? [appSelectorApp] : []}
          displayAppLogo
          isMulti={false}
          onClick={isCreation ? handleModalOpen : undefined}
        />
        <div className="empty-div" />
        <AppTemplateSelectorModal
          apps={apps}
          isOpen={isModalOpen}
          onAppRowClick={handleAppRowClick}
          onClose={handleModalClose}
          onTemplateClick={handleTemplateClick}
          selectedApp={selectedApp}
        />
        <ResetWarningModal
          isOpen={resetModalOpen}
          mainText="Change template"
          onClose={handleResetModalClose}
          onConfirm={handleResetModalConfirm}
          subText="Changing the template will reset all placeholders."
        />
      </div>
    </div>
  )
}

export default DynamicScreenHeader
